import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc, setDoc, updateDoc, Timestamp, collection, query, where, getDocs } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase-config';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, Mail, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

type InvitationStatus = 'loading' | 'valid' | 'expired' | 'not_found' | 'already_accepted' | 'email_mismatch';
type AcceptanceStatus = 'idle' | 'accepting' | 'success' | 'error';

interface Invitation {
  id: string;
  customerId: string;
  email: string;
  invitedBy: string;
  invitedByName: string;
  role: string;
  status: string;
  createdAt: any;
  expiresAt: any;
  acceptedAt?: any;
}

interface Customer {
  id: string;
  companyName: string;
  slug: string;
  brandColor: string;
  logoUrl?: string;
}

export default function AcceptInvitation() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = searchParams.get('token');
  const customerId = searchParams.get('customerId');

  const [invitationStatus, setInvitationStatus] = useState<InvitationStatus>('loading');
  const [acceptanceStatus, setAcceptanceStatus] = useState<AcceptanceStatus>('idle');
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token || !customerId) {
      setInvitationStatus('not_found');
      return;
    }

    fetchInvitationAndCustomer();
  }, [token, customerId]);

  useEffect(() => {
    // If user is logged in and we have a valid invitation, check email match
    if (user && invitation && invitationStatus === 'valid') {
      if (user.email?.toLowerCase() !== invitation.email.toLowerCase()) {
        setInvitationStatus('email_mismatch');
      }
    }
  }, [user, invitation, invitationStatus]);

  const fetchInvitationAndCustomer = async () => {
    if (!token || !customerId) return;

    try {
      // Fetch invitation
      const invitationDoc = await getDoc(doc(db, 'customers', customerId, 'invitations', token));

      if (!invitationDoc.exists()) {
        setInvitationStatus('not_found');
        return;
      }

      const invitationData = {
        id: invitationDoc.id,
        ...invitationDoc.data(),
      } as Invitation;

      setInvitation(invitationData);

      // Check if already accepted
      if (invitationData.status === 'accepted') {
        setInvitationStatus('already_accepted');
        return;
      }

      // Check if expired
      const expiresAt = invitationData.expiresAt?.toDate();
      if (expiresAt && expiresAt < new Date()) {
        setInvitationStatus('expired');
        return;
      }

      // Fetch customer data
      const customerDoc = await getDoc(doc(db, 'customers', customerId));
      if (customerDoc.exists()) {
        setCustomer({
          id: customerDoc.id,
          ...customerDoc.data(),
        } as Customer);
      }

      setInvitationStatus('valid');
    } catch (err) {
      console.error('Error fetching invitation:', err);
      setInvitationStatus('not_found');
    }
  };

  const handleAcceptInvitation = async () => {
    if (!user || !invitation || !customer) return;

    setAcceptanceStatus('accepting');
    setError(null);

    try {
      console.log('🔄 Starting invitation acceptance...', {
        userId: user.uid,
        userEmail: user.email,
        customerId: customer.id,
        customerSlug: customer.slug,
        invitationId: invitation.id,
      });

      // Create admin document
      const adminData = {
        id: user.uid,
        email: user.email,
        name: user.email?.split('@')[0] || 'Admin',
        role: invitation.role,
        customerId: customer.id,
        createdAt: Timestamp.now(),
      };

      console.log('📝 Creating admin document...', {
        path: `customers/${customer.id}/admins/${user.uid}`,
        data: adminData,
        authEmail: user.email,
      });

      const adminDocRef = doc(db, 'customers', customer.id, 'admins', user.uid);
      await setDoc(adminDocRef, adminData);
      console.log('✅ Admin document created successfully');

      // Verify the document was actually created
      const verifyDoc = await getDoc(adminDocRef);
      if (verifyDoc.exists()) {
        console.log('✅ Verified: Admin document exists in Firestore', {
          path: `customers/${customer.id}/admins/${user.uid}`,
          data: verifyDoc.data(),
        });
      } else {
        console.error('❌ ERROR: Admin document was NOT created despite success!');
        throw new Error('Admin document creation failed verification');
      }

      // Double-check by querying using slug (same way CustomerAdminRoute does it)
      console.log('🔍 Double-checking admin access using slug:', customer.slug);
      const customersRef = collection(db, 'customers');
      const q = query(customersRef, where('slug', '==', customer.slug));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const foundCustomerId = snapshot.docs[0].id;
        console.log('📋 Customer found by slug:', {
          slug: customer.slug,
          foundCustomerId,
          originalCustomerId: customer.id,
          match: foundCustomerId === customer.id,
        });

        const checkAdminDoc = await getDoc(doc(db, 'customers', foundCustomerId, 'admins', user.uid));
        console.log('🔍 Admin document check by slug:', {
          path: `customers/${foundCustomerId}/admins/${user.uid}`,
          exists: checkAdminDoc.exists(),
          data: checkAdminDoc.exists() ? checkAdminDoc.data() : null,
        });
      }

      // Mark invitation as accepted
      console.log('📝 Updating invitation status to accepted...');
      await updateDoc(doc(db, 'customers', customer.id, 'invitations', invitation.id), {
        status: 'accepted',
        acceptedAt: Timestamp.now(),
      });
      console.log('✅ Invitation status updated to accepted');

      setAcceptanceStatus('success');

      console.log('🎉 Invitation accepted successfully! Redirecting to:', `/${customer.slug}/admin`);

      // Redirect to customer admin portal after 2 seconds
      setTimeout(() => {
        navigate(`/${customer.slug}/admin`);
      }, 2000);
    } catch (err: any) {
      console.error('❌ Error accepting invitation:', err);
      const errorMessage = err?.code === 'permission-denied'
        ? 'Permission denied. You may not have access to create admin records.'
        : `Failed to accept invitation: ${err?.message || 'Unknown error'}`;
      setError(errorMessage);
      setAcceptanceStatus('error');
      toast.error(errorMessage);
    }
  };

  if (invitationStatus === 'loading') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (invitationStatus === 'not_found') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card border border-border rounded-lg p-8 text-center space-y-4">
          <XCircle className="h-16 w-16 text-destructive mx-auto" />
          <h1 className="text-2xl font-bold text-foreground">Invitation Not Found</h1>
          <p className="text-muted-foreground">
            This invitation link is invalid or has been removed.
          </p>
          <Button onClick={() => navigate('/')} variant="outline">
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  if (invitationStatus === 'expired') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card border border-border rounded-lg p-8 text-center space-y-4">
          <XCircle className="h-16 w-16 text-destructive mx-auto" />
          <h1 className="text-2xl font-bold text-foreground">Invitation Expired</h1>
          <p className="text-muted-foreground">
            This invitation has expired. Please contact {invitation?.invitedByName} to request a new invitation.
          </p>
          <Button onClick={() => navigate('/')} variant="outline">
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  if (invitationStatus === 'already_accepted') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card border border-border rounded-lg p-8 text-center space-y-4">
          <CheckCircle className="h-16 w-16 text-green-600 mx-auto" />
          <h1 className="text-2xl font-bold text-foreground">Already Accepted</h1>
          <p className="text-muted-foreground">
            This invitation has already been accepted.
          </p>
          {customer && (
            <Button onClick={() => navigate(`/${customer.slug}/admin`)} style={{ backgroundColor: customer.brandColor }} className="text-white">
              Go to Admin Portal
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (invitationStatus === 'email_mismatch' && user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card border border-border rounded-lg p-8 text-center space-y-4">
          <AlertTriangle className="h-16 w-16 text-yellow-600 mx-auto" />
          <h1 className="text-2xl font-bold text-foreground">Email Mismatch</h1>
          <p className="text-muted-foreground">
            This invitation was sent to <strong>{invitation?.email}</strong>, but you're signed in as <strong>{user.email}</strong>.
          </p>
          <p className="text-sm text-muted-foreground">
            Please sign out and sign in with the correct email address.
          </p>
          <div className="flex gap-2">
            <Button onClick={signOut} variant="outline" className="flex-1">
              Sign Out
            </Button>
            <Button onClick={() => navigate('/')} variant="outline" className="flex-1">
              Go Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Valid invitation - show acceptance UI
  if (acceptanceStatus === 'success') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card border border-border rounded-lg p-8 text-center space-y-4">
          <CheckCircle className="h-16 w-16 text-green-600 mx-auto" />
          <h1 className="text-2xl font-bold text-foreground">Welcome to the Team!</h1>
          <p className="text-muted-foreground">
            You're now an admin for <strong>{customer?.companyName}</strong>.
          </p>
          <p className="text-sm text-muted-foreground">
            Redirecting to the admin portal...
          </p>
          <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card border border-border rounded-lg p-8 space-y-6">
          {customer?.logoUrl && (
            <div className="flex justify-center">
              <img src={customer.logoUrl} alt={customer.companyName} className="h-16 object-contain" />
            </div>
          )}
          <div className="text-center space-y-2">
            <Mail className="h-12 w-12 mx-auto" style={{ color: customer?.brandColor || '#6366f1' }} />
            <h1 className="text-2xl font-bold text-foreground">You're Invited!</h1>
            <p className="text-muted-foreground">
              <strong>{invitation?.invitedByName}</strong> has invited you to join the admin team at{' '}
              <strong>{customer?.companyName}</strong>.
            </p>
          </div>

          <div className="bg-muted/50 border border-border rounded p-4 space-y-2">
            <p className="text-sm font-medium text-foreground">You'll be able to:</p>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>Manage feature requests</li>
              <li>Update request statuses</li>
              <li>Submit on behalf of users</li>
              <li>Access analytics and insights</li>
            </ul>
          </div>

          <div className="space-y-3">
            <p className="text-sm text-muted-foreground text-center">
              Sign in with <strong>{invitation?.email}</strong> to accept this invitation.
            </p>
            <Button
              onClick={() => {
                // Store the current invitation URL to redirect back after authentication
                const redirectUrl = window.location.pathname + window.location.search;
                console.log('💾 Storing redirect URL for after auth:', redirectUrl);
                window.localStorage.setItem('redirectAfterAuth', redirectUrl);
                navigate(`/login?email=${encodeURIComponent(invitation?.email || '')}`);
              }}
              style={{ backgroundColor: customer?.brandColor || '#6366f1' }}
              className="w-full text-white"
            >
              Sign In to Accept
            </Button>
          </div>

          <p className="text-xs text-muted-foreground text-center">
            This invitation will expire on {invitation?.expiresAt?.toDate().toLocaleDateString()}.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-card border border-border rounded-lg p-8 space-y-6">
        {customer?.logoUrl && (
          <div className="flex justify-center">
            <img src={customer.logoUrl} alt={customer.companyName} className="h-16 object-contain" />
          </div>
        )}
        <div className="text-center space-y-2">
          <Mail className="h-12 w-12 mx-auto" style={{ color: customer?.brandColor || '#6366f1' }} />
          <h1 className="text-2xl font-bold text-foreground">Accept Invitation</h1>
          <p className="text-muted-foreground">
            You've been invited to join <strong>{customer?.companyName}</strong> as an admin.
          </p>
        </div>

        <div className="bg-muted/50 border border-border rounded p-4 space-y-2">
          <p className="text-sm font-medium text-foreground">As an admin, you'll be able to:</p>
          <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
            <li>View and manage all feature requests</li>
            <li>Update request statuses and priorities</li>
            <li>Submit requests on behalf of users</li>
            <li>Cast votes and leave comments</li>
            <li>Access analytics and insights</li>
            <li>Configure branding and email settings</li>
          </ul>
        </div>

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <Button
            onClick={handleAcceptInvitation}
            disabled={acceptanceStatus === 'accepting'}
            style={{ backgroundColor: customer?.brandColor || '#6366f1' }}
            className="w-full text-white"
          >
            {acceptanceStatus === 'accepting' ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Accepting...
              </>
            ) : (
              'Accept Invitation'
            )}
          </Button>
        </div>

        <p className="text-xs text-muted-foreground text-center">
          Invited by {invitation?.invitedByName} • Expires {invitation?.expiresAt?.toDate().toLocaleDateString()}
        </p>
      </div>
    </div>
  );
}
